/* 
  Documentation:
  It requires 2 params to work
  1. filterBy 2. selectedID

  Gets WorkOrders using filterBy
  Then sets selectedWorkOrder by going through data received
  having same id as selectedID in param.

 */
import WOWorkflow from "./WOWorkflow/WOWorkflow";
import WOPdfButton from "components/Pdfs/WOPdf/WOPdfButton";
import WoStatusOptions from "components/WoStatusOptions";
import Spinner from "components/Spinner";
import useWorkOrder from "features/Contexts/WorkOrderContext";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState, useRef } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import DuplicateWO from "./DuplicateWO";
import WOFiles from "./files/WOFiles";
import WOApproval from "./LotApproval/WOApproval";
import ManageWOs from "./ManageWOs";
import WOProducts from "./WOProducts/WOProducts";
import WoCuttingPdfButton from "components/Pdfs/WoCuttingPdf/WoCuttingPdfButton";
import SendWoApproval from "./LotApproval/SendWoApproval";
import SendWoFeedback from "./LotApproval/SendWoFeedback";
import ExportProductPngs from "./ExportProductPngs";
import ExportWorkOrderZip from "./ExportWorkOrderZip";
import QuickViewProductSummary from "components/QuickViewWorkOrder/QuickViewProductSummary";

function DetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  /*   ALL STATES
   ********************************************* */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Persist navigation-provided workOrderIds so they aren't lost if `location` updates
  const navWorkOrderIdsRef = useRef(location?.state?.workOrderIds ?? null);

  useEffect(() => {
    if (location?.state?.workOrderIds && !navWorkOrderIdsRef.current) {
      navWorkOrderIdsRef.current = location.state.workOrderIds;
    }
  }, [location]);

  const [searchUrl, setSearchUrl] = useState("");

  const user = useSelector((state) => state.user?.userInfo);
  const disableForDataEntry = user?.permissions?.includes(
    "work_orders_dataEntry",
  );

  const { clearWorkOrderData, setCustomerData } = useWorkOrder();

  const [active, setActive] = useState(
    disableForDataEntry ? "wOProducts" : "WODetails",
  );
  const [loading, setLoading] = useState(false);
  const [allWorkOrdersData, setAllWorkOrdersData] = useState(null);
  const [WOStatus, setWOStatus] = useState(null);

  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const [wOProductsData, setWOProductsData] = useState(null);
  const [formData, setFormData] = useState(null);

  const [sowDuplicateModal, setSowDuplicateModal] = useState(false);

  const [wOFiles, setWOFiles] = useState(null);
  // Player Numbering Sheet attached (staff or customer)? Shown first in the
  // W-Attachments tab count, "(sheet-files)" (user, 2026-11-03).
  const [hasNumberingSheet, setHasNumberingSheet] = useState(false);
  const getNumberingSheet = async () => {
    if (!selectedWorkOrder?.woId) return;
    try {
      const { data } = await API.get("/wo-lot-approvals/" + selectedWorkOrder.woId, {
        headers: authHeader(),
      });
      const a = data?.success ? data.data : null;
      setHasNumberingSheet(
        !!((a?.approvalExcelFile && a.approvalExcelFile !== "0") || a?.customerExcelFile),
      );
    } catch {
      setHasNumberingSheet(false);
    }
  };
  useEffect(() => {
    setHasNumberingSheet(false);
    getNumberingSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkOrder?.woId]);
  const [allProducts, setAllProducts] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showProductSummaryModal, setShowProductSummaryModal] = useState(false);
  const [productTypeSummary, setProductTypeSummary] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [productTypesList, setProductTypesList] = useState([]);

  /*   ALL FUNCTIONS
   ********************************************* */

  const checkActive = (val) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", val);
      return newParams.toString();
    });
  };

  function getWOBodyData(searchOptions, activeTab) {
    let page = searchOptions.get("page");
    let pageLimit = searchOptions.get("limit");
    let query = searchOptions.get("query");
    let location = searchOptions.get("location");
    let body = { page, activeTab, pageLimit };

    if (query) {
      body.query = query;
    }
    if (location) {
      body.location = location;
    }
    return body;
  }

  const getWorkOrders = async () => {
    setLoading(true);
    try {
      const sourceIds = navWorkOrderIdsRef.current;
      // console.log("navWorkOrderIdsRef.current:", sourceIds);

      let activeTab = params?.filterBy;
      let currentID = params?.selectedID;

      // If caller passed a list of IDs in navigation state, fetch those records
      if (sourceIds && sourceIds.length) {
        const dataList = await getWorkOrdersByIds(sourceIds);
        if (dataList) {
          setAllWorkOrdersData(dataList);
          const matchedOrderById = dataList.find(
            (item) => item?.woId === parseInt(currentID),
          );
          if (matchedOrderById) {
            setSelectedWorkOrder(matchedOrderById);
            setLoading(false);
            return dataList;
          } else {
            const WOById = await getWorkOrderById(currentID);
            setSelectedWorkOrder(WOById);
            setLoading(false);
            return dataList;
          }
        }
        // if getWorkOrdersByIds failed, fall through to normal list fetch
      }

      let body = getWOBodyData(searchParams, activeTab);

      // check for sort and order in searchParams
      let sort = searchParams.get("sort");
      let order = searchParams.get("order");
      if (sort && order) {
        body.sort = sort;
        body.order = order;
      }

      const { data } = await API.post("/work-orders", body, {
        headers: authHeader(),
      });
      setAllWorkOrdersData(data.result);
      let matchedOrderById = data?.result?.find(
        (item) => item?.woId === parseInt(currentID),
      );
      if (matchedOrderById) {
        setSelectedWorkOrder(matchedOrderById);
        setLoading(false);
        return data.result;
      } else {
        let WOById = await getWorkOrderById(currentID);
        setSelectedWorkOrder(WOById);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  // Safe wrapper to avoid overwriting a caller-provided list (via navigation state)
  // If navigation provided `workOrderIds` we prefer that list and skip fetching
  const refreshWorkOrders = async () => {
    return await getWorkOrders();
  };

  // Fetches fresh, complete data for the currently selected work order by ID.
  // Use this instead of refreshWorkOrders when child components save data,
  // to avoid stale/incomplete data from the list endpoints.
  const refreshSelectedWorkOrder = async () => {
    const currentID = params?.selectedID;
    if (!currentID) return;
    try {
      const freshWO = await getWorkOrderById(currentID);
      if (freshWO) {
        setSelectedWorkOrder(freshWO);
        // Keep allWorkOrdersData in sync for prev/next navigation
        setAllWorkOrdersData(
          (prev) =>
            prev?.map((wo) => (wo.woId === freshWO.woId ? freshWO : wo)) ??
            prev,
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getWorkOrderById = async (id) => {
    setLoading(true);
    try {
      const { data } = await API.get("/work-order/" + id, {
        headers: authHeader(),
      });
      return data?.result;
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const getWorkOrdersByIds = async (ids) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/work-orders/by-ids",
        { ids },
        { headers: authHeader() },
      );
      if (data?.success) {
        setLoading(false);
        return data?.result;
      } else {
        setLoading(false);
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch work orders",
            type: "error",
          }),
        );
      }
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const updateWorkOrderData = async (e) => {
    e.preventDefault();

    let shipDate = formData?.WoShipDate;

    // Required Fields = jobName, WoDueDate, ShipmentMethod
    if (!formData?.jobName) {
      dispatch(
        setNotification({
          message: "Job Name is required",
          type: "error",
        }),
      );
      return;
    }
    if (!formData?.shipmentMethod) {
      dispatch(
        setNotification({
          message: "Shipment Method is required",
          type: "error",
        }),
      );
      return;
    }

    // if (!formData?.WoDueDate) {
    //   dispatch(
    //     setNotification({
    //       message: "Needed By Date is required",
    //       type: "error",
    //     })
    //   );
    //   return;
    // }

    let contactData = { ...formData };
    delete contactData.jobName;
    delete contactData.WoDueDate;
    // contactData is the WHOLE form minus two keys, so anything added to
    // formData lands inside it. The shipping address is a column of its own,
    // not contact detail -- without this it would be nested in the contact
    // blob and the card would never see it.
    delete contactData.shippingAddress;

    let body = {
      jobName: formData?.jobName,
      subJobName: formData?.subJobName,
      // WoDueDate: formData?.WoDueDate,
      shipmentMethod: formData?.shipmentMethod,
      contactData,
      WOStatus,
      WoShipDate: shipDate === "" ? null : shipDate,
    };

    // Only when the form is carrying one. WOinfo puts it there when the
    // shipment method is switched to (or away from) pickup; every other save
    // must leave the stored address untouched, since this form does not edit
    // it -- the Shipping Address card does.
    if (formData?.shippingAddress) {
      body.shippingAddress = formData.shippingAddress;
    }

    // if (shipDate && shipDate !== "") {
    //   body.WoShipDate = getDate(shipDate);
    // }

    setLoading(true);

    try {
      const { data } = await API.patch(
        "/work-orders",
        {
          data: body,
          id: selectedWorkOrder.id,
        },
        { headers: authHeader() },
      );

      if (data.message.type === "success") {
        await refreshSelectedWorkOrder();

        setLoading(false);
        return;
      }
      setLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const getWorkOrdersProducts = useCallback(
    async (wOId) => {
      setLoading(true);
      try {
        const { data } = await API.get(`/wo-products/${wOId}`, {
          headers: authHeader(),
        });

        setWOProductsData(data);
        setLoading(false);
        return data;
      } catch (err) {
        setLoading(false);
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          }),
        );
      }
    },
    [dispatch],
  );

  const prevWorkOrder = () => {
    let curWOId = params.selectedID;

    let checkCrrIdExists = allWorkOrdersData?.find(
      (item) => item?.woId === parseInt(curWOId),
    );

    if (!checkCrrIdExists) {
      return;
    }
    let currentIndex = allWorkOrdersData?.findIndex(
      (item) => item?.woId === selectedWorkOrder?.woId,
    );
    if (currentIndex === 0) {
      return;
    }
    let newId = allWorkOrdersData[currentIndex - 1]?.woId;

    navigate(`/work-order/details/${params?.filterBy}/${newId}/${searchUrl}`);
  };

  const nextWorkOrder = () => {
    let curWOId = params.selectedID;
    let checkCrrIdExists = allWorkOrdersData?.find(
      (item) => item?.woId === parseInt(curWOId),
    );
    if (!checkCrrIdExists) {
      return;
    }
    let currentIndex = allWorkOrdersData?.findIndex(
      (item) => item?.woId === selectedWorkOrder?.woId,
    );
    let totalLength = allWorkOrdersData?.length;
    if (currentIndex === totalLength - 1) {
      return;
    }
    let newId = allWorkOrdersData[currentIndex + 1]?.woId;
    navigate(`/work-order/details/${params?.filterBy}/${newId}/${searchUrl}`);
  };

  const handleBack = () => {
    searchParams.delete("tab");
    navigate(`/work-orders/${params?.filterBy}?${searchParams.toString()}`);
    clearWorkOrderData();
  };

  const handleViewCustomer = () => {
    setCustomerData({ id: selectedWorkOrder?.customerId });
    navigate(`/customer/details/${selectedWorkOrder?.customer?.customerId}`);
  };

  const getSearchParams = () => {
    let paramsUrl = "";
    searchParams.forEach((value, key) => {
      paramsUrl += `${key}=${value}&`;
    });

    setSearchUrl(`?${paramsUrl.slice(0, -1)}`);
    return `?${paramsUrl.slice(0, -1)}`;
  };

  const getAllProducts = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/wo-products/" + selectedWorkOrder?.id, {
        headers: authHeader(),
      });
      if (data?.success) {
        setAllProducts(data?.data);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  //get Data
  const getWOFiles = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "wo-file/listfiles",
        {
          id: selectedWorkOrder?.id,
        },
        { headers: authHeader() },
      );

      setWOFiles(data);
      setLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const getParsedShippingAddress = (address) => {
    if (!address) return null;

    let parsedAddress = address;

    // Keep parsing until we get an object or null
    while (typeof parsedAddress === "string") {
      try {
        parsedAddress = JSON.parse(parsedAddress);
      } catch (error) {
        console.error("Failed to parse shipping address:", error);
        return null;
      }
    }

    return parsedAddress;
  };

  const validateShipAddress = (selectedWo) => {
    return true; // Temporarily disable validation
    // console.log(selectedWo);

    let _shipAddress = selectedWo?.shippingAddress;
    if (!_shipAddress || _shipAddress === "") {
      return false;
    }

    _shipAddress = getParsedShippingAddress(_shipAddress);

    if (!_shipAddress || Object.keys(_shipAddress).length === 0) {
      return false;
    }

    // check for required fields
    let requiredFields = ["addressLine1", "city", "state", "zip", "country"];

    let isValid = true;
    requiredFields.forEach((field) => {
      if (
        !_shipAddress[field] ||
        _shipAddress[field] === "" ||
        _shipAddress[field] === null
      ) {
        isValid = false;
      }
    });

    return isValid;
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    if (selectedWorkOrder) {
      setWOStatus(selectedWorkOrder.WOStatus);
    }
  }, [selectedWorkOrder]);

  useEffect(() => {
    if (
      params?.filterBy &&
      params?.selectedID &&
      selectedWorkOrder?.woId !== parseInt(params?.selectedID)
    ) {
      let timeout = setTimeout(() => {
        getWorkOrders();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [params, location]);

  useEffect(() => {
    getSearchParams();
    let tab = searchParams.get("tab");
    if (disableForDataEntry && !tab) {
      setSearchParams((prev) => {
        return [...prev, ["tab", "wo-products"]];
      });
      return setActive("wOProducts");
    }

    let tabList = {
      "wo-details": "WODetails",
      "wo-products": "wOProducts",
      "lot-approval": "lot-approval",
      summary: "summary",
      files: "files",
      "team-view": "teamView",
      tracking: "tracking",
      payments: "payments",
      "product-costing": "productCosting",
      commission: "commission",
      workflow: "workflow",
    };

    if (tab) {
      setActive(tabList[tab]);
    } else {
      setSearchParams((prev) => {
        return [...prev, ["tab", "wo-details"]];
      });
      setActive("wo-details");
    }
  }, [searchParams]);

  useEffect(() => {
    // If navigation provided a source list (from other modules), prefer it for prev/next
    if (location?.state?.workOrdersList && !allWorkOrdersData) {
      setAllWorkOrdersData(location.state.workOrdersList);
    }

    if (selectedWorkOrder) {
      let timeout = setTimeout(() => {
        if (
          !selectedWorkOrder?.jobName ||
          !validateShipAddress(selectedWorkOrder) ||
          !selectedWorkOrder?.shipmentMethod
        ) {
          setSearchParams((prev) => {
            return {
              ...prev,
              tab: "wo-details",
            };
          });
        } else {
          getWorkOrdersProducts(selectedWorkOrder?.id);
          getWOFiles();
        }
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [selectedWorkOrder]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedWorkOrder?.id) {
        getAllProducts();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWorkOrder]);

  return (
    <div
      className="d-flex flex-grow-1 "
      style={{
        borderRadius: "10px",
        position: "fixed",
        top: 0,
        width: "calc(100vw - 180px)",
        paddingLeft: 80,
        marginLeft: 180,
        zIndex: 101,
      }}
    >
      {loading && <Spinner />}

      <div className="d-flex flex-grow-1 flex-column">
        <div className="bg-white px-4 shadow-lg py-2 d-flex justify-content-between align-items-center ">
          <div className="">
            <h5 className="d-flex align-items-center mb-1 gap-2">
              <div className="">W-{selectedWorkOrder?.woId}</div>
              <div className="font-weight-bold">
                {selectedWorkOrder?.jobName?.toUpperCase()}
              </div>
            </h5>
            <div>
              <div className="mt-0 d-flex align-items-center">
                <button
                  onClick={handleViewCustomer}
                  className="btn btn-sm btn-info me-2 py-0 px-1 mb-1 "
                >
                  View
                </button>
                {exportLoading && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 999999,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 16,
                    }}
                  >
                    <Spinner />
                    <div
                      style={{
                        color: "#fff",
                        fontSize: 18,
                        fontWeight: 600,
                        marginTop: 8,
                      }}
                    >
                      Preparing ZIP — this may take a moment...
                    </div>
                  </div>
                )}

                <div className="mt-0 d-flex align-items-center gap-2">
                  <div className="h5">
                    C-{selectedWorkOrder?.customer?.customerId}
                  </div>
                  <div className="font-weight-bold h5">
                    {selectedWorkOrder?.customer?.customerName?.toUpperCase()}
                  </div>
                  <span className="font-weight-bold h5">
                    PO Required: &nbsp;
                    {typeof selectedWorkOrder?.customer?.poRequired ===
                    "boolean"
                      ? selectedWorkOrder?.customer?.poRequired
                        ? "Yes"
                        : "No"
                      : "No Set"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center ">
            <h5 className="text-nowrap me-2">WO Status</h5>
            <select
              onChange={(e) => setWOStatus(e.target.value)}
              value={WOStatus || ""}
              className="form-select"
              id="commissionType"
            >
              {/* Settings -> WO Statuses (2026-10-29) */}
              <WoStatusOptions current={WOStatus} />
            </select>

            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="no-icon px-1 ms-1"
                id="dropdown-basic"
              >
                <i className="mdi mdi-dots-vertical "></i>
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  width: 180,
                }}
              >
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      <WOPdfButton
                        btnClasses={"btn w-100 text-start text-nowrap"}
                        btnTitle={"Print Pdf"}
                        selectedWO={selectedWorkOrder}
                      />
                    </Dropdown.Item>
                  )}
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      <SendWoApproval
                        woId={selectedWorkOrder.woId}
                        refreshFunc={() => {}}
                        btnClass={"btn w-100 text-start"}
                        btnTitle={"Ask a Question"}
                        useQuestionTemplate={true}
                      />
                    </Dropdown.Item>
                  )}
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      {/* <WOOrderEmail
                        btnClass={"btn w-100 text-start text-nowrap"}
                        btnTitle={"Email"}
                        selectedWorkOrder={selectedWorkOrder}
                        wOProductsData={wOProductsData}
                      /> */}
                      <SendWoApproval
                        woId={selectedWorkOrder.woId}
                        // selectedWO={item?.WorkOrder}
                        // woProducts={item?.WorkOrder?.WOProducts}
                        // approvalData={item}
                        refreshFunc={() => {}}
                        btnClass={"btn w-100 text-start"}
                        btnTitle={"Send Approval"}
                        // This page loads the work order without its approval
                        // row, but WorkOrders.approvalStatus is the server's
                        // mirror of allApproved, written by every path that
                        // changes it.
                        customerApproved={!!selectedWorkOrder?.approvalStatus}
                      />
                    </Dropdown.Item>
                  )}
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      <SendWoFeedback
                        woId={selectedWorkOrder.woId}
                        refreshFunc={() => {}}
                        btnClass={"btn w-100 text-start"}
                        btnTitle={"Get Feedback"}
                      />
                    </Dropdown.Item>
                  )}
                {/* Ready to Pickup email (user, 2026-10-27): the location's
                    Ready for Pickup template. */}
                {selectedWorkOrder?.woId && (
                  <Dropdown.Item as="div" className="p-0 m-0">
                    <SendWoFeedback
                      variant="ready-pickup"
                      woId={selectedWorkOrder.woId}
                      refreshFunc={() => {}}
                      btnClass={"btn w-100 text-start"}
                      btnTitle={"Ready to Pickup"}
                    />
                  </Dropdown.Item>
                )}
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      <WoCuttingPdfButton
                        btnClasses={"btn w-100 text-start text-nowrap"}
                        btnTitle={"Print Pdf"}
                        selectedWO={selectedWorkOrder}
                      />
                    </Dropdown.Item>
                  )}
                {wOProductsData &&
                  !user?.permissions?.includes("work_orders_dataEntry") && (
                    <Dropdown.Item as="div" className="p-0 m-0">
                      <ExportProductPngs
                        selectedWO={selectedWorkOrder}
                        btnClasses={"btn w-100 text-start text-nowrap"}
                        onLoadingChange={setExportLoading}
                      />
                    </Dropdown.Item>
                  )}
                {!user?.permissions?.includes("work_orders_dataEntry") && (
                  <Dropdown.Item as="div" className="p-0 m-0">
                    <ExportWorkOrderZip
                      selectedWO={selectedWorkOrder}
                      btnClasses={"btn w-100 text-start text-nowrap"}
                      onLoadingChange={setExportLoading}
                    />
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>

            {active === "WODetails" && (
              <button
                onClick={updateWorkOrderData}
                className="btn btn-success ms-1"
              >
                Save
              </button>
            )}

            <button
              onClick={() => {
                prevWorkOrder();
              }}
              className={"btn btn-success btn-sm p-0 px-1 mx-1"}
            >
              <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
            </button>
            <button
              onClick={() => {
                nextWorkOrder();
              }}
              className={"btn btn-success btn-sm p-0 px-1 me-1"}
            >
              <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
            </button>

            <button onClick={handleBack} className={"btn btn-danger"}>
              BACK
            </button>
          </div>
        </div>

        <div className="d-flex flex-grow-1">
          <div className="flex-grow-1  ">
            <div className="bg-white mx-2 custom-shadow mt-2 custom-border-radius-sm px-3 d-flex justify-content-between align-items-center">
              <ul className="nav nav-tabs nav-bordered border-0 d-flex flex-grow-1 py-2">
                <li
                  onClick={() => {
                    if (!disableForDataEntry) {
                      checkActive("wo-details");
                    }
                  }}
                  className="nav-item"
                >
                  <button
                    className={
                      active === "WODetails"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">W-Details</span>
                  </button>
                </li>
                <li
                  onClick={() => {
                    if (
                      selectedWorkOrder?.jobName &&
                      validateShipAddress(selectedWorkOrder) &&
                      selectedWorkOrder?.shipmentMethod
                    ) {
                      checkActive("wo-products");
                    } else {
                      if (!selectedWorkOrder?.jobName) {
                        alert("Please add a job name and save");
                        return;
                      }
                      if (!validateShipAddress(selectedWorkOrder)) {
                        alert("Please add a ship address and save");
                        return;
                      }
                      if (!selectedWorkOrder?.shipmentMethod) {
                        alert("Please add a shipment method and save");
                        return;
                      }
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    // open product summary modal with current products
                    setProductTypeSummary(allProducts || []);
                    setProductTypesList([]);
                    setSelectedProductType(null);
                    setShowProductSummaryModal(true);
                  }}
                  className=""
                >
                  <button
                    className={
                      active === "wOProducts"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">
                      W-Products (
                      {selectedWorkOrder && allProducts && allProducts?.length})
                    </span>
                  </button>
                </li>

                <li
                  onClick={() => {
                    if (
                      selectedWorkOrder?.jobName &&
                      validateShipAddress(selectedWorkOrder) &&
                      selectedWorkOrder?.shipmentMethod
                    ) {
                      checkActive("files");
                    } else {
                      if (!selectedWorkOrder?.jobName) {
                        alert("Please add a job name and save");
                        return;
                      }
                      if (!validateShipAddress(selectedWorkOrder)) {
                        alert("Please add a ship address and save");
                        return;
                      }
                      if (!selectedWorkOrder?.shipmentMethod) {
                        alert("Please add a shipment method and save");
                        return;
                      }
                    }
                  }}
                  className="nav-item"
                >
                  <button
                    className={
                      active === "files"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">
                      W-Attachments ({hasNumberingSheet ? 1 : 0}-{wOFiles?.length ?? 0})
                    </span>
                  </button>
                </li>
                <li
                  onClick={() => {
                    if (
                      selectedWorkOrder?.jobName &&
                      validateShipAddress(selectedWorkOrder) &&
                      selectedWorkOrder?.shipmentMethod
                    ) {
                      checkActive("lot-approval");
                    } else {
                      if (!selectedWorkOrder?.jobName) {
                        alert("Please add a job name and save");
                        return;
                      }
                      if (!validateShipAddress(selectedWorkOrder)) {
                        alert("Please add a ship address and save");
                        return;
                      }
                      if (!selectedWorkOrder?.shipmentMethod) {
                        alert("Please add a shipment method and save");
                        return;
                      }
                    }
                  }}
                  className="nav-item"
                >
                  <button
                    className={
                      active === "lot-approval"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">W-Approval</span>
                  </button>
                </li>

                {/* No Tracking tab (user, 2026-10-27): it never had content. */}
                <li
                  onClick={() => {
                    if (
                      selectedWorkOrder?.jobName &&
                      validateShipAddress(selectedWorkOrder) &&
                      selectedWorkOrder?.shipmentMethod
                    ) {
                      if (!disableForDataEntry) checkActive("payments");
                    } else {
                      if (!selectedWorkOrder?.jobName) {
                        alert("Please add a job name and save");
                        return;
                      }
                      if (!validateShipAddress(selectedWorkOrder)) {
                        alert("Please add a ship address and save");
                        return;
                      }
                      if (!selectedWorkOrder?.shipmentMethod) {
                        alert("Please add a shipment method and save");
                        return;
                      }
                    }
                  }}
                  className="nav-item"
                >
                  <button
                    className={
                      active === "payments"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">Payments</span>
                  </button>
                </li>
                {/* Workflow (user, 2026-11-02): where each product is in
                    its product type's workflow. */}
                <li onClick={() => checkActive("workflow")} className="nav-item">
                  <button
                    className={
                      active === "workflow"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <span className="d-none d-md-block">Workflow</span>
                  </button>
                </li>
              </ul>
              <div>
                <button
                  onClick={() => {
                    setSowDuplicateModal(true);
                  }}
                  className="btn btn-success"
                >
                  Duplicate This workOrder
                </button>
              </div>
            </div>
            <div className="px-2 mt-2">
              {active === "WODetails" && (
                <div className="tab-pane show active">
                  {selectedWorkOrder && (
                    <ManageWOs
                      formData={formData}
                      setFormData={setFormData}
                      refreshFunc={refreshSelectedWorkOrder}
                      updateFunc={updateWorkOrderData}
                      selectedWorkOrder={selectedWorkOrder}
                    />
                  )}
                </div>
              )}
              {active === "wOProducts" && (
                <div className="tab-pane show active">
                  {selectedWorkOrder && (
                    <WOProducts
                      selectedWorkOrder={selectedWorkOrder}
                      allProducts={allProducts}
                      setAllProducts={setAllProducts}
                      refreshFunc={getAllProducts}
                    />
                  )}
                </div>
              )}
              {active === "files" && (
                <div className="tab-pane show active">
                  {selectedWorkOrder && wOFiles && (
                    <WOFiles
                      onApprovalChange={getNumberingSheet}
                      selected={selectedWorkOrder}
                      wOFiles={wOFiles}
                      setWOFiles={setWOFiles}
                      getWOFiles={getWOFiles}
                    />
                  )}
                </div>
              )}
              {active === "workflow" && selectedWorkOrder && (
                <div className="tab-pane show active">
                  <WOWorkflow selectedWorkOrder={selectedWorkOrder} />
                </div>
              )}
              {active === "lot-approval" && (
                <div className="tab-pane show active">
                  <WOApproval
                    selectedWO={selectedWorkOrder}
                    woProducts={allProducts}
                    refreshProducts={getAllProducts}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*   Duplicate WorkOrder
       ********************************************* */}
      <Modal
        show={showProductSummaryModal}
        onHide={() => setShowProductSummaryModal(false)}
        contentClassName="custom-shadow-sm custom-border-radius px-2"
        size="lg"
      >
        <QuickViewProductSummary
          productTypeSummary={productTypeSummary}
          selected={selectedWorkOrder}
          allProducts={allProducts}
          setAllProducts={setAllProducts}
          setProductsList={setWOProductsData}
          setSelectedProduct={() => {}}
          setIsAllReady={() => {}}
          loading={loading}
          loadingOn={() => setLoading(true)}
          loadingOff={() => setLoading(false)}
          refreshFunc={getAllProducts}
          productTypesList={productTypesList}
          selectedProductType={selectedProductType}
          setProductTypeSummary={setProductTypeSummary}
          setSelectedProductType={setSelectedProductType}
        />
      </Modal>
      {/* xl since the dialog lists the products (ten columns). At that width
          it reaches under the sidebar, which is fixed at z-index 99999
          (NavBar.js) -- so the modal and its backdrop sit above it, the same
          ladder as the Nova Shipping and Line Item Report modals. Its nested
          contact picker already sits higher still (1000000002). */}
      <style>
        {`
          .duplicate-wo-backdrop { z-index: 100000; }
          .duplicate-wo-modal    { z-index: 100001; }
        `}
      </style>
      <Modal
        size="xl"
        show={sowDuplicateModal}
        onHide={() => setSowDuplicateModal(false)}
        scrollable
        className="duplicate-wo-modal"
        backdropClassName="duplicate-wo-backdrop"
        contentClassName="bg-white shadow-lg custom-border-radius"
      >
        {/* No Modal.Body wrapper and no heading here on purpose -- the dialog
            supplies its own Header/Body/Footer so that `scrollable` pins the
            title and the Duplicate button while only the fields scroll. Same
            arrangement as the Build A Find modal. */}
        {allProducts && (
          <DuplicateWO
            searchUrl={searchUrl}
            selectedWorkOrder={selectedWorkOrder}
            allProducts={allProducts}
            showModal={setSowDuplicateModal}
          />
        )}
      </Modal>
    </div>
  );
}

export default DetailsPage;
